import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Button from '../Button';

export default class DropdownBase extends React.PureComponent {
    static propTypes = {
        /** Id used to create the aria-controls pairing with the button and popup */
        id: PropTypes.string.isRequired,

        /** The button that toggles the popup. Must be able to handle receiving
         * these props: id, isDisabled, innerRef, onClick, and multiple aria props */
        triggerButtonElement: PropTypes.element.isRequired,

        /** Makes the trigger button disabled */
        isDisabled: PropTypes.bool,

        /** Sets the top position of the popup as 8px more than the height of
         * each button size */
        size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),

        /** Contents of the popup */
        children: PropTypes.node,

        /** The text of the apply button; usually "Apply" or "Close" */
        applyButtonLabel: PropTypes.string,

        /** Content that is placed at the bottom left of the popup - directly
         * across from the apply button. */
        popupLeftActionElement: PropTypes.node,

        /** Callback fired when the user clicks the apply button */
        onApply: PropTypes.func,

        /** Callback fired when the user closes the popup. This can be for any
         * reason including: the user clicked outside the popup, tabbed away
         * from the dropdown or clicked the apply button */
        onClose: PropTypes.func,

        /** Displays the dropdown bottom actions section */
        showPopupActions: PropTypes.bool,

        /** Ref that points to the trigger button. Must use React.createRef() */
        innerRef: PropTypes.shape({
            current: PropTypes.node
        }),

        /** Additional class applied to the outer container */
        className: PropTypes.string,

        /** change the data-component prop to a custom value */
        dataComponent: PropTypes.string
    };

    static defaultProps = {
        size: 'md',
        isDisabled: false,
        onApply: undefined,
        onClose: undefined,
        applyButtonLabel: 'Apply',
        popupLeftActionElement: undefined,
        showPopupActions: true,
        innerRef: undefined
    };

    constructor(props) {
        super(props);

        this.wrapperRef = React.createRef();
        this.buttonRef = props.innerRef || React.createRef();

        this.state = {
            isExpanded: false
        }
    }

    componentDidMount() {
        document.addEventListener('keyup', this.handleKeyup);
        document.addEventListener('click', this.handleDocumentClick);
    }

    componentWillUnmount() {
        document.removeEventListener('keyup', this.handleKeyup);
        document.removeEventListener('click', this.handleDocumentClick);
    }

    handleTriggerClick = (event) => {
        event.preventDefault();

        const { isExpanded } = this.state;

        if (isExpanded) {
            this.closePopup();
        }
        else {
            this.setState({
                isExpanded: true
            });
        }
    }

    handleKeyup = (event) => {
        if (this.state.isExpanded) {
            const key = event.key || event.which;

            if (key === 'Escape' || key === 'Esc' || key === 27) {
                event.preventDefault();
                this.closePopup();

                const isButtonRefWorking = !!this.buttonRef?.current?.focus;

                if (isButtonRefWorking) {
                    this.buttonRef.current.focus();
                }
            }

            if (key === 'Tab' || key === 9) {
                const isWrapperRefWorking = !!this.wrapperRef?.current?.contains;
                if (isWrapperRefWorking && !this.wrapperRef.current.contains(document.activeElement)) {
                    this.closePopup();
                }
            }
        }
    }

    handleDocumentClick = (event) => {
        const { isDisabled } = this.props;
        const { isExpanded } = this.state;
        // We're suddenly getting a bunch of ref is undefined errors from camping
        // pages using this component, so we're adding this check for safety
        const isButtonRefWorking = !!this.buttonRef?.current?.contains;
        const isWrapperRefWorking = !!this.wrapperRef?.current;
        const isEventInsideRef = isButtonRefWorking && this.buttonRef.current.contains(event.target);

        if (isDisabled || isEventInsideRef) {
            return;
        }

        if (isExpanded && isWrapperRefWorking && !this.wrapperRef.current.contains(event.target)) {
            event.preventDefault();
            this.closePopup();
        }
    }

    handleApplyClick = (event) => {
        const { onApply } = this.props;

        event.preventDefault();
        this.closePopup();
        this.buttonRef.current.focus();

        if (onApply) {
            onApply();
        }
    }

    closePopup() {
        const { onClose } = this.props;

        this.setState({
            isExpanded: false
        }, () => {
            if (onClose) {
                onClose();
            }
        });

        // close callback here
    }

    renderTriggerButton() {
        const {
            id,
            isDisabled,
            triggerButtonElement
        } = this.props;

        const { isExpanded } = this.state;

        return React.cloneElement(
            triggerButtonElement,
            {
                id,
                isDisabled,
                isExpanded,
                'aria-haspopup': "true",
                'aria-expanded': isExpanded,
                'aria-controls': `${id}-popup`,
                innerRef: this.buttonRef,
                onClick: this.handleTriggerClick
            }
        )
    }

    render() {
        const {
            id,
            children,
            size,
            applyButtonLabel,
            popupLeftActionElement,
            className,
            dataComponent,
            showPopupActions
        } = this.props;

        return (
            <div
                data-component={dataComponent || 'DropdownBase'}
                className={cx('sarsa-dropdown-base', className)}
                ref={this.wrapperRef}>

                {this.renderTriggerButton()}

                {/* outer stays in dom so aria-controls always has a target */}
                <div id={`${id}-popup`}>
                    {this.state.isExpanded && (
                        <div
                            data-component="DropdownBase-popup"
                            className={cx('sarsa-dropdown-base-popup', {
                                'size-sm': size === 'xs' || size === 'sm',
                                'size-md': size === 'md',
                                'size-lg': size === "lg"
                            })}>
                            {children}
                            {showPopupActions && (
                                <div className="sarsa-dropdown-base-popup-actions" data-component="DropdownBase-actions">
                                    <div className="sarsa-dropdown-base-popup-actions-content">
                                        <div>
                                            {popupLeftActionElement}
                                        </div>
                                        <Button
                                            appearance="link"
                                            onClick={this.handleApplyClick}>
                                            {applyButtonLabel}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
